import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Trophy, Medal, Award, TrendingUp, Target, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GameLeaderboard = ({ user }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [myScores, setMyScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    gameType: '',
    difficulty: ''
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Load leaderboard
      const leaderboardParams = new URLSearchParams();
      if (filters.gameType) leaderboardParams.append('game_type', filters.gameType);
      if (filters.difficulty) leaderboardParams.append('difficulty', filters.difficulty);
      
      const leaderboardResponse = await axios.get(
        `${API}/api/game-scores/leaderboard?${leaderboardParams}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeaderboard(leaderboardResponse.data);

      // Load my scores
      const myScoresParams = new URLSearchParams();
      if (filters.gameType) myScoresParams.append('game_type', filters.gameType);
      
      const myScoresResponse = await axios.get(
        `${API}/api/game-scores/my-scores?${myScoresParams}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyScores(myScoresResponse.data);
      
    } catch (error) {
      console.error('Error loading leaderboard:', error);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return null;
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-white';
    if (rank === 3) return 'bg-gradient-to-r from-amber-500 to-amber-600 text-white';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg">
          <Trophy className="w-8 h-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Game Leaderboard
          </h2>
          <p className="text-gray-600 text-sm">Top performers across all games</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Game Type</label>
            <Select value={filters.gameType} onValueChange={(value) => setFilters({ ...filters, gameType: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Games" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Games</SelectItem>
                <SelectItem value="flash_number">Flash Number</SelectItem>
                <SelectItem value="math_operations">Math Operations</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Difficulty</label>
            <Select value={filters.difficulty} onValueChange={(value) => setFilters({ ...filters, difficulty: value })}>
              <SelectTrigger>
                <SelectValue placeholder="All Difficulties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Difficulties</SelectItem>
                <SelectItem value="easy">Easy</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="hard">Hard</SelectItem>
                <SelectItem value="1_digit">1 Digit</SelectItem>
                <SelectItem value="2_digit">2 Digits</SelectItem>
                <SelectItem value="3_digit">3 Digits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Tabs defaultValue="leaderboard" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="mystats">My Stats</TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard" className="space-y-4 mt-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No scores yet. Be the first to play!</div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry) => (
                <Card 
                  key={entry.user_id} 
                  className={`p-4 ${entry.user_id === user.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold ${getRankBadge(entry.rank)}`}>
                      {getRankIcon(entry.rank) || `#${entry.rank}`}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">
                          {entry.user_name}
                          {entry.user_id === user.id && (
                            <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded">You</span>
                          )}
                        </h4>
                      </div>
                      <div className="text-sm text-gray-600">
                        {entry.total_games} games played
                      </div>
                    </div>

                    <div className="text-right space-y-1">
                      <div className="text-2xl font-bold text-blue-600">
                        {entry.best_accuracy.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500">
                        Best: {entry.best_score} correct
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mystats" className="space-y-6 mt-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : !myScores || !myScores.stats ? (
            <Card className="p-12 text-center">
              <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No games played yet</h3>
              <p className="text-gray-600">Start playing to see your stats here!</p>
            </Card>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
                  <div className="text-center">
                    <Target className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-blue-600">{myScores.stats.total_games}</div>
                    <div className="text-sm text-gray-600">Games Played</div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
                  <div className="text-center">
                    <Trophy className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-green-600">{myScores.stats.total_correct}</div>
                    <div className="text-sm text-gray-600">Total Correct</div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
                  <div className="text-center">
                    <TrendingUp className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-purple-600">{myScores.stats.average_accuracy.toFixed(1)}%</div>
                    <div className="text-sm text-gray-600">Avg Accuracy</div>
                  </div>
                </Card>
                <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100">
                  <div className="text-center">
                    <Award className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                    <div className="text-3xl font-bold text-amber-600">{myScores.stats.total_questions}</div>
                    <div className="text-sm text-gray-600">Questions</div>
                  </div>
                </Card>
              </div>

              {/* Recent Games */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Recent Games</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {myScores.scores.map((score, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded ${
                          score.game_type === 'flash_number' 
                            ? 'bg-purple-100 text-purple-600' 
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {score.game_type === 'flash_number' ? <Zap className="w-5 h-5" /> : <Target className="w-5 h-5" />}
                        </div>
                        <div>
                          <div className="font-medium">
                            {score.game_type === 'flash_number' ? 'Flash Number' : 'Math Operations'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(score.created_at).toLocaleDateString()} • {score.difficulty}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{score.accuracy.toFixed(1)}%</div>
                        <div className="text-xs text-gray-500">{score.score}/{score.total_questions}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GameLeaderboard;
